import { colors, FelaCSS, spacing } from '@bridebook/ui';
import { IThemeProperties } from '@bridebook/web/lib/wedding-website/utils/get-theme-properties';

interface IStyles {
  substepWrapper: FelaCSS;
  alreadySubmittedInfoWrapper: FelaCSS;
  continueButton: FelaCSS;
  sectionTitle: FelaCSS;
  errorMessageWrapper: FelaCSS;
  events: {
    nameWrapper: FelaCSS;
    name: FelaCSS;
    listWrapper: FelaCSS;
    listTitle: FelaCSS;
    listItem: FelaCSS;
    listItemName: FelaCSS;
    listItemControls: FelaCSS;
    listItemControlsButton: FelaCSS;
    listItemNameEvent: FelaCSS;
    listItemNameCaption: FelaCSS;
  };
  questions: {
    questionWrapper: FelaCSS;
    question: FelaCSS;
    answerOption: FelaCSS;
    answerOptionsWrapper: FelaCSS;
  };
  note: {
    nameWrapper: FelaCSS;
    name: FelaCSS;
    noteWrapper: FelaCSS;
    label: FelaCSS;
  };
  summary: {
    title: FelaCSS;
    subtitle: FelaCSS;
    nameWrapper: FelaCSS;
    sectionsWrapper: FelaCSS;
    eventsListWrapper: FelaCSS;
    eventName: FelaCSS;
    eventCaption: FelaCSS;
    eventListItem: FelaCSS;
    eventListItemDetails: FelaCSS;
    eventListItemAnswer: FelaCSS;
    eventAnswer: (isYes: boolean) => FelaCSS;
    questionsListWrapper: FelaCSS;
    question: FelaCSS;
    note: FelaCSS;
    buttonWrapperResubmission: FelaCSS;
    buttonWrapper: FelaCSS;
  };
  confirmation: {
    title: FelaCSS;
    image: FelaCSS;
    partyDescription: FelaCSS;
    partyList: FelaCSS;
  };
  deadlinePassed: {
    infoWrapper: FelaCSS;
  };
}

const componentStyles: (themeProperties: IThemeProperties, isPreview?: boolean) => IStyles = (
  themeProperties,
  isPreview = false,
) => ({
  substepWrapper: {
    paddingVertical: spacing[8],
    paddingHorizontal: spacing[4],
    ...(!isPreview && {
      minWidthMobileLg: {
        paddingHorizontal: spacing[16],
      },
    }),
    height: '100%',
  },
  alreadySubmittedInfoWrapper: {
    maxWidth: '360px',
    textAlign: 'center',
    justifySelf: 'center',
    alignSelf: 'center',
    paddingBottom: spacing[6],
    minWidthMobileLg: {
      paddingBottom: spacing[8],
    },
  },
  continueButton: {
    alignSelf: 'center',
    minWidthMobileLg: {
      maxWidth: '270px',
    },
  },
  sectionTitle: {
    fontDefaultSemiBold: 16,
    lineHeight: 1.2,
    color: colors.space,
  },
  errorMessageWrapper: {
    scrollMarginTop: '120px',
    paddingVertical: spacing[4],
    minWidthDesktop: {
      scrollMarginTop: '0px',
    },
  },
  events: {
    nameWrapper: {
      color: colors.space,
      lineHeight: 1.6,
      fontSize: 14,
      textAlign: 'center',
      alignSelf: 'center',
      width: '100%',
      maxWidth: '360px',
    },
    name: {
      fontSize: 16,
      fontWeight: 600,
    },
    listTitle: {
      color: colors.space,
      fontWeight: 700,
      paddingTop: spacing[6],
      paddingBottom: spacing[4],
      fontSize: 16,
      alignSelf: 'center',
      width: '100%',
      maxWidth: '360px',
    },
    listWrapper: {
      paddingBottom: spacing[4],
      flexDirection: 'column',
      gap: spacing[4],
      fontSize: 14,
      alignSelf: 'center',
      width: '100%',
      maxWidth: '360px',
      minWidthMobileLg: {
        fontSize: 16,
      },
    },
    listItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing[4],
      color: colors.space,
    },
    listItemName: {
      flex: 1,
    },
    listItemNameEvent: {
      fontSize: 16,
    },
    listItemNameCaption: {
      fontSize: 14,
      color: colors.space75,
    },
    listItemControls: {
      width: '140px',
      flexDirection: 'row',
      gap: spacing[2],
      height: 'auto',
      alignItems: 'flex-start',
    },
    listItemControlsButton: {
      paddingVertical: spacing[2],
      height: '40px',
      borderRadius: '40px',
    },
  },
  questions: {
    questionWrapper: {
      flexDirection: 'column',
      gap: spacing[4],
      color: colors.space,
      maxWidth: '340px',
      alignSelf: 'center',
      width: '100%',
    },
    question: {
      fontSize: 16,
      fontWeight: 600,
      textAlign: 'center',
    },
    answerOptionsWrapper: {
      flexDirection: 'row',
      gap: spacing[4],
      height: '64px',
    },
    answerOption: {
      paddingVertical: spacing[2],
      height: '40px',
      borderRadius: '40px',
    },
  },
  note: {
    nameWrapper: {
      paddingBottom: spacing[2],
      color: colors.space,
      lineHeight: 1.6,
      fontSize: 14,
      textAlign: 'center',
      alignSelf: 'center',
      width: '100%',
      maxWidth: '360px',
      minWidthMobileLg: {
        paddingBottom: spacing[4],
      },
    },
    name: {
      fontSize: 16,
      fontWeight: 600,
    },
    noteWrapper: {
      paddingVertical: spacing[2],
      alignSelf: 'center',
      width: '100%',
      maxWidth: '360px',
      minWidthMobileLg: {
        paddingVertical: spacing[4],
      },
    },
    label: {
      color: colors.space,
      fontWeight: 700,
      paddingTop: spacing[6],
      paddingBottom: spacing[6],
      fontSize: 16,
      alignSelf: 'center',
      width: '100%',
      maxWidth: '360px',
    },
  },
  summary: {
    title: {
      fontFamily: themeProperties.primaryFontStyle.fontFamily,
      fontWeight: themeProperties.primaryFontStyle.fontWeight,
      fontSize: 24,
      color: colors.space,
      textAlign: 'center',
      paddingBottom: spacing[10],
      minWidthMobileLg: {
        fontSize: 48,
      },
    },
    sectionsWrapper: {
      gap: spacing[10],
      minWidthMobileLg: {
        gap: spacing[12],
      },
    },
    nameWrapper: {
      fontSize: 16,
    },
    subtitle: {
      fontSize: 16,
      color: colors.space,
      fontWeight: 600,
      lineHeight: 1.75,
    },
    eventsListWrapper: {
      fontSize: 14,
      paddingTop: spacing[1],
      gap: spacing[6],
      minWidthMobileLg: {
        gap: spacing[8],
      },
    },
    eventListItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing[4],
    },
    eventListItemDetails: {
      flex: 1,
    },
    eventListItemAnswer: {
      width: '60px',
      minWidthMobileLg: {
        width: '80px',
      },
    },
    eventName: {
      fontWeight: 400,
      lineHeight: 1.6,
      fontSize: 16,
    },
    eventCaption: {
      color: colors.space75,
    },
    eventAnswer: (isYes) => ({
      fontSize: 14,
      textAlign: 'center',
      height: '32px',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.white,
      backgroundColor: isYes ? colors.indigoCrush : colors.space40,
      borderRadius: '32px',
      minWidthMobileLg: {
        height: '40px',
        borderRadius: '40px',
      },
    }),
    questionsListWrapper: {
      gap: spacing[6],
      fontSize: 16,
      minWidthMobileLg: {
        gap: spacing[8],
      },
    },
    question: {
      fontWeight: 600,

      lineHeight: 1.75,
    },
    note: {
      fontSize: 16,
      whiteSpace: 'pre-wrap',
    },
    buttonWrapper: {
      gap: spacing[2],
      alignItems: 'center',
    },
    buttonWrapperResubmission: {
      gap: spacing[2],
      width: '100%',
      alignItems: 'center',
      minWidthMobileLg: {
        flexDirection: 'row',
        gap: spacing[4],
        justifyContent: 'center',
      },
    },
  },
  confirmation: {
    title: {
      fontFamily: themeProperties.primaryFontStyle.fontFamily,
      fontWeight: themeProperties.primaryFontStyle.fontWeight,
      fontSize: 24,
      lineHeight: 1.33,
      color: colors.space,
      textAlign: 'center',
      paddingBottom: spacing[6],
      minWidthMobileLg: {
        fontSize: 32,
      },
    },
    image: {
      width: '100px',
      margin: '0 auto',
      justifyContent: 'center',
      minWidthMobileLg: {
        width: '120px',
      },
    },
    partyDescription: {
      fontSize: 14,
      color: colors.space75,
      textAlign: 'center',
      paddingTop: spacing[6],
      paddingBottom: spacing[4],
      paddingHorizontal: spacing[8],
    },
    partyList: {
      flexDirection: 'column',
      gap: spacing[4],
      paddingBottom: spacing[6],
      paddingHorizontal: spacing[4],
      minWidthMobileLg: {
        paddingHorizontal: spacing[8],
      },
    },
  },
  deadlinePassed: {
    infoWrapper: {
      textAlign: 'center',
    },
  },
});

export default componentStyles;
